import * as XLSX from 'xlsx';
import type { ImportRow, RoastType } from '../components/inventory/ImportRoastedBulkModal/types';
import { ROAST_TYPES } from '../components/inventory/ImportRoastedBulkModal/types';

export const MAX_IMPORT_ROWS = 500;

const HEADER_ORDER = [
  'Source Green Bean',
  'Stock (kg)',
  'Standard Cost',
  'Roast Type',
] as const;

export interface ParseResult {
  rows: ImportRow[];
}

/**
 * Map of GB code → id, supplied by the caller. Built from the picker's
 * selected GBs and threaded through Upload → Review.
 */
export interface GBCodeIndex {
  byCode: Map<string, { id: string; name: string }>;
}

export function parseRoastedBulkImportWorkbook(
  wb: XLSX.WorkBook,
  index: GBCodeIndex,
): ParseResult {
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('Workbook has no sheets');
  const sheet = wb.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json<(string | number)[]>(sheet, {
    header: 1,
    defval: '',
  });
  if (aoa.length === 0) throw new Error('Sheet is empty');

  const headerRow = aoa[0].map((h) => String(h).trim());
  for (const required of HEADER_ORDER) {
    if (!headerRow.includes(required)) {
      throw new Error(`Missing required column: ${required}`);
    }
  }
  const colIdx: Record<(typeof HEADER_ORDER)[number], number> = {
    'Source Green Bean': headerRow.indexOf('Source Green Bean'),
    'Stock (kg)': headerRow.indexOf('Stock (kg)'),
    'Standard Cost': headerRow.indexOf('Standard Cost'),
    'Roast Type': headerRow.indexOf('Roast Type'),
  };

  const dataRows = aoa.slice(1);
  if (dataRows.length > MAX_IMPORT_ROWS) {
    throw new Error(`Too many rows (max ${MAX_IMPORT_ROWS})`);
  }

  const rows: ImportRow[] = dataRows
    .filter((r) => r.some((cell) => String(cell).trim() !== ''))
    .map((rawRow) => buildRow(rawRow, colIdx, index));

  return { rows };
}

function buildRow(
  rawRow: (string | number)[],
  col: Record<(typeof HEADER_ORDER)[number], number>,
  index: GBCodeIndex,
): ImportRow {
  const sourceCell = String(rawRow[col['Source Green Bean']] ?? '').trim();
  let sourceGreenBeanItemId = '';
  let sourceGreenBeanLabel = sourceCell;
  if (sourceCell) {
    const code = sourceCell.split(' — ')[0]?.trim() ?? '';
    const hit = index.byCode.get(code);
    if (hit) {
      sourceGreenBeanItemId = hit.id;
      sourceGreenBeanLabel = `${code} — ${hit.name}`;
    }
  }

  const stockRaw = rawRow[col['Stock (kg)']];
  const stock = typeof stockRaw === 'number' ? stockRaw : parseFloat(String(stockRaw).trim());

  const costRaw = rawRow[col['Standard Cost']];
  const cost = typeof costRaw === 'number' ? costRaw : parseFloat(String(costRaw).trim());

  const rtCell = String(rawRow[col['Roast Type']] ?? '').trim();
  const normalized = rtCell ? rtCell.charAt(0).toUpperCase() + rtCell.slice(1).toLowerCase() : '';
  const roastType: RoastType | '' = (ROAST_TYPES as readonly string[]).includes(normalized)
    ? (normalized as RoastType)
    : '';

  return {
    sourceGreenBeanItemId,
    sourceGreenBeanLabel,
    initialQty: Number.isFinite(stock) ? stock : 0,
    standardCost: Number.isFinite(cost) ? cost : 0,
    roastType,
  };
}

export async function parseRoastedBulkImportFile(
  file: File,
  index: GBCodeIndex,
): Promise<ParseResult> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  return parseRoastedBulkImportWorkbook(wb, index);
}
